import styles from '@/components/Studies/StudyExports/ExportReadyToast/ExportReadyToast.module.scss';
import { TYPE_LABEL } from '@/components/Studies/StudyExports/ExportsList/Exports.model';
import { triggerBrowserDownload } from '@/components/Studies/StudyExports/StudyExports.utils';
import { errorToast } from '@/helpers.tsx';
import { _notNil } from '@/littledash';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { type ExportGeneratedEventV1 } from '@/model/TeamEvent.model';
import { type FC, useCallback, useState } from 'react';
import { RiFileDownloadLine } from 'react-icons/ri';

interface ExportReadyToastProps {
  event: ExportGeneratedEventV1;
  onDismiss?: () => void;
}

const ExportReadyToast: FC<ExportReadyToastProps> = ({ event, onDismiss }) => {
  const [downloading, setDownloading] = useState(false);

  const { invoke: invokeStudyScopedDownload } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/exports/{exportApiId}/download-url',
    options: { invokeOnInit: false, onError: { toast: false, throw: true, slug: 'toast-export-download-study' } },
  });

  const { invoke: invokeTeamScopedDownload } = useApiHook({
    endpoint: 'GET /api/v1/exports/{exportApiId}/download-url',
    options: { invokeOnInit: false, onError: { toast: false, throw: true, slug: 'toast-export-download-team' } },
  });

  const handleDownload = useCallback(async () => {
    setDownloading(true);
    try {
      const result = _notNil(event.study_api_id)
        ? await invokeStudyScopedDownload({
            path: {
              studyApiId: event.study_api_id,
              exportApiId: event.export_api_id,
            },
          })
        : await invokeTeamScopedDownload({
            path: { exportApiId: event.export_api_id },
          });

      const { url, file_name: fileName } = result.body ?? {};
      if (!_notNil(url)) {
        errorToast('Could not get download link.');
        return;
      }

      const response = await fetch(url);
      if (!response.ok) {
        errorToast('Could not download export.');
        return;
      }
      const blob = await response.blob();
      triggerBrowserDownload(blob, fileName ?? 'export');
      onDismiss?.();
    } catch {
      errorToast('Could not download export.');
    } finally {
      setDownloading(false);
    }
  }, [event, invokeStudyScopedDownload, invokeTeamScopedDownload, onDismiss]);

  return (
    <div className="flex flex-column" data-testid="export-ready-toast">
      <div className="mb1" data-testid="export-ready-toast__label">
        <strong>{TYPE_LABEL[event.export_type]} export ready</strong>
      </div>
      <button
        type="button"
        className={styles.download}
        onClick={handleDownload}
        disabled={downloading}
        data-testid="export-ready-toast__download"
      >
        <RiFileDownloadLine />
        Download export
      </button>
    </div>
  );
};

export default ExportReadyToast;
